import React, { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/formfields/TextInput";
import { SelectDropdown } from "@/components/formfields/SelectDropdown";
import type { SkillDetail, SkillPayload } from "@/interface/masterManagement";
import { useCreateSkill, useUpdateSkill } from "../hooks/useSkill";
import { useCategoryList } from "../hooks/useCategory";

const skillSchema = z.object({
  categoryId: z.string().min(1, "Category is required"),
  name: z
    .string()
    .trim()
    .min(1, "Skill Name is required")
    .max(255, "Skill Name cannot exceed 255 characters"),
});

export type SkillFormValues = z.infer<typeof skillSchema>;

interface SkillFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  editData?: SkillDetail | null;
}

export const SkillFormModal: React.FC<SkillFormModalProps> = ({
  isOpen,
  onClose,
  editData,
}) => {
  const createMutation = useCreateSkill();
  const updateMutation = useUpdateSkill();

  const { data: categoryData } = useCategoryList({ page_size: 100 });
  const categories = categoryData?.data?.results || [];
  const categoryOptions = useMemo(
    () =>
      categories.map((cat) => ({
        label: cat.skill_category_name || "",
        value: String(cat.id),
      })),
    [categories],
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<SkillFormValues>({
    resolver: zodResolver(skillSchema),
    defaultValues: {
      categoryId: "",
      name: "",
    },
  });

  useEffect(() => {
    if (isOpen) {
      if (editData) {
        const catId = String(
          editData.category ?? editData.category_detail?.id ?? "",
        );
        reset({
          categoryId: catId,
          name: editData.skill_name,
        });
      } else {
        reset({
          categoryId: categories.length > 0 ? String(categories[0].id) : "",
          name: "",
        });
      }
    }
  }, [isOpen, editData, reset, categories]);

  const onSubmit = async (values: SkillFormValues) => {
    try {
      const payload: SkillPayload = {
        category: Number(values.categoryId),
        skill_name: values.name,
      };

      if (editData) {
        await updateMutation.mutateAsync({ id: editData.id, payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      onClose();
    } catch {
      // Error handled by query
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800">
          <DialogTitle>{editData ? "Edit Skill" : "Add Skill"}</DialogTitle>
        </DialogHeader>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col flex-1 overflow-hidden"
        >
          <div className="flex-1 overflow-y-auto p-6 space-y-5 bg-slate-50/50 dark:bg-slate-900/50">
            {/* Category Dropdown Select */}
            <SelectDropdown
              control={control}
              name="categoryId"
              label="Category"
              placeholder="Select a Category"
              options={categoryOptions}
              required
              isSearchable
            />

            <TextInput
              control={control}
              name="name"
              label="Skill Name"
              placeholder="e.g. React.js"
              required
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                isSubmitting ||
                createMutation.isPending ||
                updateMutation.isPending
              }
            >
              {isSubmitting ||
              createMutation.isPending ||
              updateMutation.isPending
                ? "Saving..."
                : editData
                  ? "Save Changes"
                  : "Add Skill"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SkillFormModal;
